<template>
    <kiosk-migration-layout :migrationId="props.migrationId">
        <div class="flex flex-col w-full max-w-(--breakpoint-sm) justify-center">
            <page-header>Details</page-header>

            <vx-expandable-card class="mb-6">
                <template v-slot:content>
                    <div class="text-xl font-extrabold mb-4">Migration Information</div>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col gap-1">
                            <strong>Migration ID:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-badge size="xs" color="slate">{{ migration.id }}</vx-badge>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Status:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-badge :color="statusColor" size="xs">
                                    {{ statusLabel }}
                                </vx-badge>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Target Team:</strong>
                            <div class="text-sm text-slate-500 flex items-center gap-2">
                                <vx-badge size="xs" color="slate">{{ migration.target_team_id }}</vx-badge>
                                <span>{{ migration.target_team_name }}</span>
                                <vx-button
                                    color="muted"
                                    size="xs"
                                    :href="`/kiosk/teams/${migration.target_team_id}`"
                                    data-test="go-to-target-team"
                                >
                                    View Team
                                    <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Created:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-popover hover :arrow="true" placement="top" :key="createdAtKey">
                                    {{ fromNow(migration.created_at) }}
                                    <template v-slot:content>
                                        {{ shortDateTime(migration.created_at) }}
                                    </template>
                                </vx-popover>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Updated:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-popover hover :arrow="true" placement="top" :key="updatedAtKey">
                                    {{ fromNow(migration.updated_at) }}
                                    <template v-slot:content>
                                        {{ shortDateTime(migration.updated_at) }}
                                    </template>
                                </vx-popover>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
        </div>
    </kiosk-migration-layout>
</template>

<script setup>
import { computed } from 'vue';
import { VxBadge, VxButton, VxExpandableCard, VxPopover } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { useInterval } from '@vueuse/core';
import PageHeader from '~/components/general/PageHeader.vue';
import KioskMigrationLayout from './KioskMigrationLayout.vue';
import { useMigrationDetails } from '../composables/useMigrationDetails';
import { getMigrationStatusLabel } from '~/utils/migrationHelpers';
import { fromNow, shortDateTime } from '~/utils/date';

const props = defineProps({
    migrationId: {
        type: Number,
        required: true,
    },
});

const { migration } = useMigrationDetails();

const createdAtKey = useInterval(60000);
const updatedAtKey = useInterval(60000);

const statusColor = computed(() => {
    const colorMap = {
        draft: 'slate',
        in_progress: 'blue',
        canceling: 'orange',
        completed: 'green',
        canceled: 'red',
    };
    return colorMap[migration.value?.status] || 'slate';
});

const statusLabel = computed(() => getMigrationStatusLabel(migration.value?.status));
</script>
